import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useState } from 'react';
import { AVAILABILITY_DATE_FORMAT } from '@domain/fulfillment-settings/blocs/order-availability';
import { Button } from '@/components/button';
import { Calendar } from '@usepepper/ui/drafts/calendar';
import { formatDateByWeekday, formatSummaryDate } from '@/utils/date';
import {
  GET_RESTAURANT_VP_SUBSCRIPTIONS,
  RESTAURANT_VP_SUBSCRIPTION_FRAGMENT,
} from '@/screens/subscriptions/queries';
import { getOperationName } from 'apollo-utilities';
import { gql, useMutation } from '@apollo/client';
import { Moment } from 'moment/moment';
import {
  PauseVp_SubscriptionMutation,
  PauseVp_SubscriptionMutationVariables,
} from '@/types';
import { SCREEN_PADDING } from '@/styles';
import { StyleSheet, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useToasts } from '@/contexts/toast-context';

export const PAUSE_RESTAURANT_SUBSCRIPTION_MUTATION = gql`
  mutation PauseVP_Subscription(
    $restaurantUUID: uuid!
    $restaurantVPSubscriptionUUID: uuid!
    $resumeAt: String!
    $supplierUUID: uuid!
  ) {
    pauseRestaurantVariantPackSubscription(
      restaurant_id: $restaurantUUID
      restaurant_variant_pack_subscription_id: $restaurantVPSubscriptionUUID
      resume_at: $resumeAt
      supplier_id: $supplierUUID
    ) {
      restaurant_variant_pack_subscription {
        ...RestaurantVP_Subscription
      }
    }
  }

  ${RESTAURANT_VP_SUBSCRIPTION_FRAGMENT}
`;

interface Props {
  frequencyDays: number;
  nextFulfillmentDate: Moment;
  nextPlacementDate?: string;
  onClose: () => void;
  subscriptionUUID: string;
}

export const SubscriptionPauseUntil: React.FC<Props> = ({
  frequencyDays,
  nextFulfillmentDate,
  nextPlacementDate,
  onClose,
  subscriptionUUID,
}) => {
  const earliestPossibleDay = moment(nextFulfillmentDate).add(
    frequencyDays,
    'days',
  );

  const [selectedDate, setSelectedDate] = useState(earliestPossibleDay);
  const formattedSelectedDate = formatSummaryDate(selectedDate);
  const isDateDisabled = useCallback(
    (date: Moment) => {
      const differenceDays = date.diff(nextFulfillmentDate, 'days');
      return (
        date.isBefore(earliestPossibleDay) ||
        differenceDays % frequencyDays !== 0
      );
    },
    [earliestPossibleDay, frequencyDays, nextFulfillmentDate],
  );

  const nextPossiblePlacementDate = useMemo(() => {
    const targetWeekday = moment(nextPlacementDate).weekday();
    const selectedDateMoment = moment(selectedDate).startOf('day');
    const mostRecentMatchingWeekday = moment(selectedDateMoment)
      .weekday(targetWeekday)
      .startOf('day');
    if (mostRecentMatchingWeekday.isAfter(selectedDateMoment)) {
      mostRecentMatchingWeekday.subtract(7, 'days');
    }
    return formatDateByWeekday(mostRecentMatchingWeekday);
  }, [nextPlacementDate, selectedDate]);

  const [pauseRestaurantVPSubscription, { loading }] = useMutation<
    PauseVp_SubscriptionMutation,
    PauseVp_SubscriptionMutationVariables
  >(PAUSE_RESTAURANT_SUBSCRIPTION_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      getOperationName(GET_RESTAURANT_VP_SUBSCRIPTIONS) as string,
    ],
  });
  const { showErrorToast } = useToasts();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();

  const { isSmallLayout } = useLayoutContext();
  const resumeAt = selectedDate.format(AVAILABILITY_DATE_FORMAT);

  return (
    <View style={styles.base}>
      <View style={styles.titleWrapper}>
        <Typography variant="heading-lg">
          {i18n.t('pause_until_specific_day.confirmation_title')}
        </Typography>
      </View>
      <View style={styles.calendarWrapper}>
        <Calendar
          activeDate={selectedDate}
          isDateDisabled={isDateDisabled}
          onDateChange={(activeDate) => setSelectedDate(activeDate)}
        />
      </View>
      <View style={styles.messageWrapper}>
        {nextPlacementDate ? (
          <Typography variant="paragraph-base">
            {i18n.t('next_order_placed', {
              date: nextPossiblePlacementDate,
            })}
          </Typography>
        ) : null}
      </View>
      <View style={styles.actionButtonsWrapper}>
        <Button
          loading={loading}
          onPress={async () => {
            try {
              await pauseRestaurantVPSubscription({
                variables: {
                  restaurantUUID,
                  restaurantVPSubscriptionUUID: subscriptionUUID,
                  resumeAt,
                  supplierUUID,
                },
              });
            } catch {
              showErrorToast();
            } finally {
              onClose();
            }
          }}
          trackingData={{
            name: 'subscriptions.pause_until',
            properties: {
              resumeAt,
              subscriptionUUID,
            },
          }}
        >
          {i18n.t('pause_until_specific_day.resume_on', {
            date: formattedSelectedDate,
          })}
        </Button>
        {isSmallLayout ? (
          <Button
            onPress={onClose}
            trackingData={{
              name: 'subscriptions.cancel_pause',
              properties: {
                subscriptionUUID,
              },
            }}
            type="secondary"
          >
            {i18n.t('cancel')}
          </Button>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  actionButtonsWrapper: {
    gap: 8,
  },
  base: {
    paddingHorizontal: SCREEN_PADDING,
  },
  calendarWrapper: {
    marginBottom: 24,
    marginHorizontal: -8,
  },
  messageWrapper: {
    marginBottom: 8,
  },
  titleWrapper: {
    marginBottom: 16,
  },
});
